/**
 * MediaAttachments — Renders video, audio, and image attachments on posts.
 *
 * Replaces the inline media rendering in PostCard with proper:
 * - Video player (MP4/WebM) with controls
 * - Audio player (MP3/WAV) with compact waveform-style bar
 * - Image gallery with lightbox-style click-to-expand
 * - PDF attachments (delegates to PdfAttachment)
 */

import { useState } from 'react';
import type { ClubTheme } from './themes';
import PdfAttachment from './PdfAttachment';

interface MediaItem {
  url: string;
  type: string;
  alt?: string;
  size?: number;
}

interface MediaAttachmentsProps {
  media: MediaItem[];
  theme: ClubTheme;
}

function isImage(type: string) {
  return type.startsWith('image/');
}
function isVideo(type: string) {
  return type.startsWith('video/');
}
function isAudio(type: string) {
  return type.startsWith('audio/');
}
function isPdf(type: string) {
  return type === 'application/pdf';
}

export default function MediaAttachments({ media, theme }: MediaAttachmentsProps) {
  const images = media.filter((m) => isImage(m.type));
  const videos = media.filter((m) => isVideo(m.type));
  const audios = media.filter((m) => isAudio(m.type));
  const pdfs = media.filter((m) => isPdf(m.type));
  const docs = media.filter((m) => !isImage(m.type) && !isVideo(m.type) && !isAudio(m.type) && !isPdf(m.type));

  if (media.length === 0) return null;

  return (
    <div className="mt-3 space-y-2">
      {/* Videos */}
      {videos.map((v, i) => (
        <VideoPlayer key={`video-${i}`} src={v.url} theme={theme} />
      ))}

      {/* Images */}
      {images.length > 0 && <ImageGallery images={images} theme={theme} />}

      {/* Audio */}
      {audios.map((a, i) => (
        <AudioPlayer key={`audio-${i}`} src={a.url} title={a.alt || 'Audio'} theme={theme} />
      ))}

      {/* PDFs */}
      {pdfs.map((p, i) => (
        <PdfAttachment
          key={`pdf-${i}`}
          url={p.url}
          filename={p.alt || 'Document.pdf'}
          size={p.size}
          theme={theme}
        />
      ))}

      {/* Other docs */}
      {docs.map((d, i) => (
        <a
          key={`doc-${i}`}
          href={d.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs transition-colors hover:opacity-80"
          style={{
            backgroundColor: `${theme.colors.muted}10`,
            color: theme.colors.primary,
            border: `1px solid ${theme.colors.muted}15`,
          }}
        >
          📎 {d.alt || 'Download file'}
        </a>
      ))}
    </div>
  );
}

// ─── Video Player ──────────────────────────────────────────────────

function VideoPlayer({ src, theme }: { src: string; theme: ClubTheme }) {
  return (
    <div
      className="overflow-hidden rounded-xl"
      style={{ border: `1px solid ${theme.colors.muted}15` }}
    >
      <video
        src={src}
        controls
        preload="metadata"
        className="w-full max-h-[400px] bg-black"
        style={{ borderRadius: 'inherit' }}
      >
        Your browser does not support video playback.
      </video>
    </div>
  );
}

// ─── Audio Player ──────────────────────────────────────────────────

function AudioPlayer({ src, title, theme }: { src: string; title: string; theme: ClubTheme }) {
  return (
    <div
      className="flex items-center gap-3 rounded-xl px-3 py-2"
      style={{
        backgroundColor: `${theme.colors.muted}10`,
        border: `1px solid ${theme.colors.muted}15`,
      }}
    >
      <span className="text-lg">🎵</span>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium truncate" style={{ color: theme.colors.text }}>
          {title}
        </p>
        <audio src={src} controls preload="metadata" className="mt-1 w-full h-8" />
      </div>
    </div>
  );
}

// ─── Image Gallery ─────────────────────────────────────────────────

function ImageGallery({ images, theme }: { images: MediaItem[]; theme: ClubTheme }) {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  // Layout: 1 image = full width, 2 = side by side, 3+ = grid
  const gridClass =
    images.length === 1
      ? 'grid-cols-1'
      : images.length === 2
      ? 'grid-cols-2'
      : 'grid-cols-2 sm:grid-cols-3';

  return (
    <>
      <div className={`grid ${gridClass} gap-2`}>
        {images.map((img, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setExpandedIdx(i)}
            className="overflow-hidden rounded-xl transition-transform hover:scale-[1.02]"
            style={{ border: `1px solid ${theme.colors.muted}15` }}
          >
            <img
              src={img.url}
              alt={img.alt || ''}
              className={`w-full object-cover ${
                images.length === 1 ? 'max-h-[400px]' : 'h-48'
              }`}
              loading="lazy"
            />
          </button>
        ))}
      </div>

      {/* Lightbox overlay */}
      {expandedIdx !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setExpandedIdx(null)}
        >
          <div className="relative max-h-[90vh] max-w-[90vw]" onClick={(e) => e.stopPropagation()}>
            <img
              src={images[expandedIdx].url}
              alt={images[expandedIdx].alt || ''}
              className="max-h-[85vh] max-w-[85vw] rounded-xl object-contain"
            />
            <button
              type="button"
              onClick={() => setExpandedIdx(null)}
              className="absolute -right-3 -top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-sm font-bold text-black shadow-lg"
            >
              ✕
            </button>

            {/* Nav arrows */}
            {images.length > 1 && (
              <div className="absolute inset-x-0 bottom-4 flex justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setExpandedIdx((expandedIdx - 1 + images.length) % images.length)}
                  className="rounded-full bg-white/20 px-3 py-1 text-white backdrop-blur-sm hover:bg-white/40"
                >
                  ← Prev
                </button>
                <span className="rounded-full bg-white/20 px-3 py-1 text-white backdrop-blur-sm text-sm">
                  {expandedIdx + 1} / {images.length}
                </span>
                <button
                  type="button"
                  onClick={() => setExpandedIdx((expandedIdx + 1) % images.length)}
                  className="rounded-full bg-white/20 px-3 py-1 text-white backdrop-blur-sm hover:bg-white/40"
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
